"use client"
import React, { createContext, useState, useContext, useEffect, ReactNode } from "react"
import axios from "axios"
import API from "~/api/api"
import { LocalTracksContext } from "~/components/twilio/LocalTracksProvider"
import { isMobile } from "~/utils/functions"
import useDevices from "~/composable/twilio/useDevices"
import { usePermissionState } from "~/composable/twilio/usePermissionState"
import { PERMISSION_STATE } from "~/constants/twilio"
import ZoomVideo from "@zoom/videosdk"

export enum VideoQuality {
  /**
   * 90P
   */
  Video_90P = 0,
  /**
   * 180P
   */
  Video_180P = 1,
  /**
   * 360P
   */
  Video_360P = 2,
  /**
   * 720P
   */
  Video_720P = 3,
  /**
   * 1080P
   */
  Video_1080P = 4,
}

export enum RecordingStatus {
  /**
   * Recording.
   */
  Recording = "Recording",
  /**
   * Recording paused.
   */
  Paused = "Paused",
  /**
   * Recording stopped.
   */
  Stopped = "Stopped",
  /**
   * When to start ISO recording.
   */
  Ask = "Ask",
  /**
   * When user accepts ISO recording.
   */
  Accept = "Accept",
  /**
   * When user declines ISO recording.
   */
  Decline = "Decline",
}

export type ZoomClient = ReturnType<typeof ZoomVideo.createClient>
type ZoomMediaStream = ReturnType<ZoomClient["getMediaStream"]>
interface VideoContextType {
  connect: (hash: string | string[], connecting: () => void) => Promise<void>
  mediaStream: ZoomMediaStream | null
  client: ZoomClient | null
  sendData: (dataObject: object) => void
  sessionId: string | null
  leaveSession: () => Promise<void>
  toggleVideo: () => Promise<void>
  toggleAudio: () => Promise<void>
  isRecording: boolean
  toggleRecording: () => void
  setIsRecording: (data: unknown) => void
}

export const VideoContext = createContext<VideoContextType | null>(null)

interface Props {
  children: ReactNode
}

export function VideoProvider({ children }: Props) {
  const [mediaStream, setMediaStream] = useState<ZoomMediaStream | null>(null)
  const [client, setClient] = useState<ZoomClient | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  // HungHV: have device or not
  const { hasVideoInputDevices, hasAudioInputDevices } = useDevices()
  // HungHV: have permission to use the device
  const cameraPermission = usePermissionState("camera" as PermissionName)
  const microphonePermission = usePermissionState("microphone" as PermissionName)

  const {
    videoId,
    audioId,
    toggleVideo: localToggleVideo,
    toggleAudio: localToggleAudio,
    videoEnabled,
    audioEnabled,
    localTrackLeaveSession,
  } = useContext(LocalTracksContext)

  const fetchToken = async (
    hash: string,
    sessionName: string,
    userIdentity: string
  ): Promise<string> => {
    const response = await axios.post(API.CONTRACT_VIDEO_TOKEN2.replace("{hash}", `${hash}`), {
      id: userIdentity,
      sessionName,
    })
    return response.data
  }

  async function leaveSession(): Promise<void> {
    try {
      const cloudRecording = client?.getRecordingClient()
      cloudRecording.canStartRecording() && cloudRecording.stopCloudRecording()
      mediaStream.stopVideo()
      mediaStream.stopAudio()
      client?.leave()
      localTrackLeaveSession()
    } catch (error) {
      console.error("❌ error leaveSession:", error)
    }
  }

  const sendData = (dataObject: unknown) => {
    const chatClient = client.getChatClient()
    chatClient.sendToAll(JSON.stringify(dataObject))
  }

  const registrationEvent = () => {
    // Add a listener to disconnect from the room when a user closes their browser
    window.addEventListener("beforeunload", leaveSession)

    if (isMobile) {
      // Add a listener to disconnect from the room when a mobile user closes their browser
      window.addEventListener("pagehide", leaveSession)
    }

    window.addEventListener("popstate", leaveSession)
  }

  const connect = async (hash: string, connecting: () => void): Promise<void> => {
    try {
      const sessionName = `group-room-${hash}`
      const userName = `User-${new Date().getTime().toString().slice(8)}`
      const token = await fetchToken(hash, sessionName, userName)
      // HungHV: https://developers.zoom.us/blog/angular-video-conferencing-app/
      // HungHV: https://developers.zoom.us/docs/video-sdk/twilio/web/
      // HungHV: https://developers.zoom.us/blog/nextjs-video-conferencing-app-using-the-zoom-video-sdk/
      if (typeof window !== "undefined") {
        // HungHV: join session - session is created every time a video call is made.
        const ZoomVideo = (await import("@zoom/videosdk")).default
        const client = ZoomVideo.createClient()
        // https://marketplacefront.zoom.us/sdk/custom/web/interfaces/InitOptions.html
        await client.init("en-US", "Global", {
          patchJsMedia: true,
          leaveOnPageUnload: true,
          enforceMultipleVideos: true,
        })
        connecting()
        await client.join(sessionName, token, userName)

        const mediaStream = client.getMediaStream()
        // HungHV: which camera/audio to use
        if (videoId && videoEnabled) {
          await mediaStream.startVideo({ cameraId: videoId, fullHd: true })
        }
        if (audioId) {
          await mediaStream.startAudio({ microphoneId: audioId })
          if (audioEnabled) {
            mediaStream.unmuteAudio()
          } else {
            mediaStream.muteAudio()
          }
        }
        setClient(client)
        setMediaStream(mediaStream)
        setSessionId(client.getSessionInfo().sessionId)
      }
    } catch (e) {
      console.error("connect session failure")
      throw new Error(e)
    }
  }

  const toggleVideo = async () => {
    if (mediaStream) {
      try {
        let _videoId = await localToggleVideo()
        if (videoEnabled) {
          mediaStream.stopVideo()
        } else {
          if (!_videoId) {
            console.warn("Not found camera")
            return
          }
          await mediaStream.startVideo({ cameraId: _videoId })
        }
      } catch (e) {
        console.error("Zoom toggleVideo error:", e)
      }
    }
  }

  const toggleAudio = async () => {
    if (mediaStream) {
      try {
        let _audioId = await localToggleAudio()
        if (audioEnabled) {
          mediaStream.muteAudio()
        } else {
          if (!audioId) {
            await mediaStream.startAudio({ microphoneId: _audioId })
            return
          }
          mediaStream.unmuteAudio()
        }
      } catch (e) {
        console.error("Zoom toggleAudio error:", e)
      }
    }
  }

  useEffect(() => {
    if (client && mediaStream) {
      registrationEvent()
    }
  }, [client, mediaStream])

  useEffect(() => {
    const handleCamera = async () => {
      if (!mediaStream) return
      if (!hasVideoInputDevices || cameraPermission === PERMISSION_STATE.DENIED) {
        const currentUser = client?.getCurrentUserInfo()
        if (currentUser?.bVideoOn) {
          mediaStream.stopVideo()
        }
      }
    }
    handleCamera()
  }, [cameraPermission, hasVideoInputDevices])

  useEffect(() => {
    const handleMicro = async () => {
      if (!mediaStream) return
      try {
        if (!hasAudioInputDevices || microphonePermission === PERMISSION_STATE.DENIED) {
          mediaStream.stopAudio()
        } else {
          let _audioId = await localToggleAudio()
          await mediaStream.startAudio({ microphoneId: _audioId })
        }
      } catch (e) {
        console.log("effect audio error: ", e)
      }
    }
    handleMicro()
  }, [microphonePermission, hasAudioInputDevices])

  return (
    <VideoContext.Provider
      value={{
        connect,
        mediaStream,
        client,
        sendData,
        sessionId,
        leaveSession,
        toggleVideo,
        toggleAudio,
        isRecording: false,
        toggleRecording: () => null,
        setIsRecording: () => null,
      }}
    >
      {children}
    </VideoContext.Provider>
  )
}
